import { useState } from 'react';
import { useNavigate, useParams } from 'react-router';
import { clsx } from '../../../ds/clsx';
import { Eyebrow, TextLink } from '../../../ds/components';
import type { LinkedGroup } from '../types';
import { Badge, EmptyBox } from './bits';
import styles from './record.module.css';

/** The right column of the record: Deals · Tickets · Einheiten · Portal-Vorgänge as collapsible groups. */
export function LinkedObjects({ groups }: { groups: LinkedGroup[] }) {
  const navigate = useNavigate();
  const { id: contactId } = useParams();
  const [closed, setClosed] = useState<Record<string, boolean>>({});
  return (
    <>
      {groups.map((group) => {
        const open = !closed[group.key];
        const panelId = `linked-${group.key}`;
        return (
          <section
            key={group.key}
            className={styles.card}
            style={{ padding: '18px 20px' }}
            aria-label={group.label}
          >
            <div className={styles.cardHead}>
              <button
                type="button"
                className={styles.eyebrowButton}
                aria-expanded={open}
                aria-controls={panelId}
                onClick={() => setClosed((state) => ({ ...state, [group.key]: open }))}
              >
                <Eyebrow size={11}>{group.label}</Eyebrow>
              </button>
              <span className={styles.cardCount}>{group.items.length > 0 ? group.items.length : '—'}</span>
              <button
                type="button"
                className={styles.cardCaret}
                aria-hidden="true"
                tabIndex={-1}
                onClick={() => setClosed((state) => ({ ...state, [group.key]: open }))}
              >
                {open ? '–' : '+'}
              </button>
            </div>
            {open ? (
              <div id={panelId}>
                {group.key === 'deals' && contactId ? (
                  <span className={styles.linkedCreate}>
                    <TextLink size="small" to={`/deals/new?contactId=${encodeURIComponent(contactId)}`}>
                      Deal anlegen
                    </TextLink>
                    <TextLink size="small" to={`/financing/new?contactId=${encodeURIComponent(contactId)}`}>
                      Finanzierung anlegen
                    </TextLink>
                  </span>
                ) : null}
                {group.items.length === 0 ? (
                  <EmptyBox variant="tight" title="Keine." text={group.emptyText} />
                ) : null}
                {group.items.length > 0 ? (
                  <div className={styles.linkedList}>
                    {group.items.map((item) => {
                      // Self-disclosure versions open the internal page (Selbstauskunft 08–10) of the M4 module.
                      const href =
                        item.href ??
                        (group.key === 'portal' &&
                        contactId &&
                        /^Selbstauskunft · Version \d+/.test(item.title)
                          ? `/contacts/${contactId}/self-disclosure/${item.id}`
                          : null);
                      const content = (
                        <>
                          <div className={styles.linkedTitleRow}>
                            <span className={styles.linkedTitle}>{item.title}</span>
                            {href ? (
                              <span className={styles.linkedArrow} aria-hidden="true">
                                ↗
                              </span>
                            ) : null}
                          </div>
                          {item.meta ? <div className={styles.linkedMeta}>{item.meta}</div> : null}
                          {item.status || item.value ? (
                            <div className={styles.linkedFoot}>
                              {item.status ? (
                                <Badge tone={item.status.tone}>{item.status.label}</Badge>
                              ) : null}
                              {item.value ? <span className={styles.linkedValue}>{item.value}</span> : null}
                            </div>
                          ) : null}
                        </>
                      );
                      return href ? (
                        <button
                          key={item.id}
                          type="button"
                          className={clsx(styles.linkedCard, styles.linkedCardLink)}
                          onClick={() => void navigate(href)}
                        >
                          {content}
                        </button>
                      ) : (
                        <div key={item.id} className={styles.linkedCard}>
                          {content}
                        </div>
                      );
                    })}
                  </div>
                ) : null}
              </div>
            ) : null}
          </section>
        );
      })}
    </>
  );
}
